import { useEffect, useState, useRef, useMemo } from 'react';
import { Loader } from '@googlemaps/js-api-loader';

interface UseGoogleMapsOptions {
  libraries?: string[];
  timeout?: number;
  retryAttempts?: number;
}

// Global state to prevent multiple script loads
let googleMapsPromise: Promise<void> | null = null;
let isGoogleMapsLoaded = false;

export const useGoogleMaps = (options: UseGoogleMapsOptions = {}) => {
  const [isLoaded, setIsLoaded] = useState(isGoogleMapsLoaded);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const timeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const retryCountRef = useRef(0);
  const isMountedRef = useRef(true);

  // Memoize libraries to prevent unnecessary re-renders (include marker for Advanced Markers)
  const libraries = useMemo(
    () => options.libraries || ['geometry', 'drawing', 'marker'],
    [options.libraries]
  );

  const loadGoogleMaps = async (): Promise<void> => {
    const apiKey = process.env.NEXT_PUBLIC_GOOGLE_API_KEY;

    if (!apiKey) {
      throw new Error('Google Maps API key is not configured');
    }

    const loader = new Loader({
      apiKey,
      version: 'quarterly',
      libraries: libraries as ('geometry' | 'drawing' | 'marker')[],
    });

    try {
      // Load the core maps library first
      await loader.importLibrary('maps');

      // Load additional libraries that were specified
      const libraryPromises = libraries
        .filter((lib) => lib !== 'maps') // Don't load maps twice
        .map((lib) => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          return loader.importLibrary(lib as any);
        });

      // Wait for all libraries to load
      await Promise.all(libraryPromises);

      // Ensure Map constructor is available before marking as loaded
      if (
        window.google &&
        window.google.maps &&
        window.google.maps.Map &&
        typeof window.google.maps.Map === 'function'
      ) {
        isGoogleMapsLoaded = true;
      } else {
        throw new Error('Google Maps Map constructor not available');
      }
    } catch (error) {
      throw new Error('Failed to load Google Maps API');
    }
  };

  useEffect(() => {
    isMountedRef.current = true;

    const initializeGoogleMaps = async () => {
      // Check if already loaded with more thorough validation
      if (
        window.google &&
        window.google.maps &&
        window.google.maps.Map &&
        typeof window.google.maps.Map === 'function'
      ) {
        if (isMountedRef.current) {
          setIsLoaded(true);
          isGoogleMapsLoaded = true;
        }
        return;
      }

      // Check if already loading
      if (googleMapsPromise) {
        if (isMountedRef.current) setIsLoading(true);
        try {
          await googleMapsPromise;
          if (isMountedRef.current) {
            setIsLoaded(true);
            setIsLoading(false);
          }
        } catch (err) {
          if (isMountedRef.current) {
            setError(err instanceof Error ? err.message : 'Unknown error');
            setIsLoading(false);
          }
        }
        return;
      }

      // Start loading
      if (isMountedRef.current) {
        setIsLoading(true);
        setError(null);
      }

      const maxRetries = options.retryAttempts || 3;

      while (retryCountRef.current < maxRetries) {
        try {
          // Add timeout wrapper for the official loader
          const timeoutPromise = new Promise<never>((_, reject) => {
            timeoutRef.current = setTimeout(() => {
              reject(new Error('Google Maps API loading timeout'));
            }, options.timeout || 10000);
          });

          googleMapsPromise = Promise.race([loadGoogleMaps(), timeoutPromise]);

          await googleMapsPromise;

          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
          }

          if (isMountedRef.current) {
            setIsLoaded(true);
            setIsLoading(false);
          }
          return;
        } catch (err) {
          retryCountRef.current++;
          googleMapsPromise = null;

          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
          }

          if (retryCountRef.current >= maxRetries) {
            if (isMountedRef.current) {
              setError(
                err instanceof Error
                  ? err.message
                  : 'Failed to load Google Maps'
              );
              setIsLoading(false);
            }
            return;
          }

          // Wait before retry
          await new Promise((resolve) =>
            setTimeout(resolve, 1000 * retryCountRef.current)
          );
        }
      }
    };

    initializeGoogleMaps();

    // Cleanup function
    return () => {
      isMountedRef.current = false;
      if (timeoutRef.current) {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        clearTimeout(timeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [libraries, options.timeout, options.retryAttempts]);

  return { isLoaded, isLoading, error };
};

// Type declaration for window.google
declare global {
  interface Window {
    google: typeof google;
  }
}
